import React, { useState } from 'react';
import { Box, Button, Table, TableBody, TableCell, TableRow } from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, DragIndicator as DragIcon } from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { RowContextMenu, ContextMenuOption } from '../ui/RowContextMenu';
import DataTableHeader from '../table/DataTableHeader';
import { useTablePreferences } from '../../hooks/useTablePreferences';
import { wellnessCategoryColumns } from '../../config/wellnessCategoryColumns';
import { WellnessItem } from '../../services/wellnessItemService';
import { WellnessCategory } from '../../types/wellnessCategory';

interface Props {
  categories: WellnessCategory[];
  wellnessItems: WellnessItem[];
  onAdd: () => void;
  onEdit: (category: WellnessCategory) => void;
  onDelete: (category: WellnessCategory) => void;
  onReorder: (categories: WellnessCategory[]) => void;
}

const DRAG_HANDLE_WIDTH = 32;

interface SortableRowProps {
  category: WellnessCategory;
  wellnessItems: WellnessItem[];
  visibleColumns: string[];
  getColumnWidth: (key: string) => number;
  onClick: (e: React.MouseEvent, cat: WellnessCategory) => void;
}

const SortableRow: React.FC<SortableRowProps> = ({ category, wellnessItems, visibleColumns, getColumnWidth, onClick }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: category._key ?? category.title,
  });

  const getCellValue = (key: string): React.ReactNode => {
    switch (key) {
      case 'title': return category.title;
      case 'subtitle': return category.subtitle || '—';
      case 'wellnessItem':
        return category.wellness_item_id
          ? (wellnessItems.find((w) => w.id === category.wellness_item_id)?.name ?? category.wellness_item_id)
          : '—';
      default: return '—';
    }
  };

  return (
    <TableRow
      ref={setNodeRef}
      sx={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        bgcolor: isDragging ? 'action.hover' : 'inherit',
        cursor: 'pointer',
        '&:hover': { backgroundColor: 'grey.50' },
      }}
      onClick={(e) => onClick(e, category)}
    >
      <TableCell
        sx={{ width: DRAG_HANDLE_WIDTH, minWidth: DRAG_HANDLE_WIDTH, px: 0.5, cursor: 'grab' }}
        onClick={(e) => e.stopPropagation()}
        {...attributes}
        {...listeners}
      >
        <DragIcon fontSize="small" sx={{ color: 'text.disabled', display: 'block' }} />
      </TableCell>
      {visibleColumns.map((key) => {
        const width = getColumnWidth(key);
        return (
          <TableCell
            key={key}
            sx={{
              width: `${width}px`,
              minWidth: `${width}px`,
              maxWidth: `${width}px`,
              padding: '12px 8px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {getCellValue(key)}
          </TableCell>
        );
      })}
    </TableRow>
  );
};

const WellnessCategoriesTab: React.FC<Props> = ({ categories, wellnessItems, onAdd, onEdit, onDelete, onReorder }) => {
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<WellnessCategory | null>(null);

  const tablePrefs = useTablePreferences('wellness-categories-v2', wellnessCategoryColumns);
  const visibleColumns = tablePrefs.preferences.columnOrder.filter((key) =>
    tablePrefs.preferences.visibleColumns.includes(key)
  );
  const totalWidth = DRAG_HANDLE_WIDTH + visibleColumns.reduce((sum, key) => sum + tablePrefs.getColumnWidth(key), 0);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const ids = categories.map((c) => c._key ?? c.title);

  const handleRowClick = (e: React.MouseEvent, cat: WellnessCategory) => {
    setSelectedCategory(cat);
    setMenuPosition({ top: e.clientY, left: e.clientX });
  };

  const handleMenuClose = () => {
    setMenuPosition(null);
    setSelectedCategory(null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = ids.indexOf(String(active.id));
      const newIndex = ids.indexOf(String(over.id));
      onReorder(arrayMove(categories, oldIndex, newIndex));
    }
  };

  const contextMenuOptions: ContextMenuOption[] = [
    {
      label: 'Edit',
      icon: <EditIcon fontSize="small" sx={{ mr: 1 }} />,
      onClick: () => { if (selectedCategory) onEdit(selectedCategory); },
    },
    {
      label: 'Delete',
      icon: <DeleteIcon fontSize="small" sx={{ mr: 1 }} />,
      onClick: () => { if (selectedCategory) onDelete(selectedCategory); },
      color: 'error',
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1 }}>
        <Button variant="contained" endIcon={<AddIcon />} size="small" onClick={onAdd} sx={{ gap: 1 }}>
          Add Category
        </Button>
      </Box>
      <Box sx={{ maxHeight: 300, overflowX: 'auto', overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <Table
          style={{
            width: `${totalWidth}px`,
            minWidth: `${totalWidth}px`,
            borderCollapse: 'collapse',
            backgroundColor: '#fff',
            tableLayout: 'fixed',
          }}
        >
          <DataTableHeader
            columns={[
              { key: '__drag__', label: '', defaultWidth: DRAG_HANDLE_WIDTH, minWidth: DRAG_HANDLE_WIDTH, visible: true, sortable: false, resizable: false },
              ...wellnessCategoryColumns,
            ]}
            visibleColumns={['__drag__', ...visibleColumns]}
            getColumnWidth={(key) => key === '__drag__' ? DRAG_HANDLE_WIDTH : tablePrefs.getColumnWidth(key)}
            onColumnResize={tablePrefs.updateColumnWidth}
          />
          <TableBody>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={ids} strategy={verticalListSortingStrategy}>
                {categories.map((cat) => (
                  <SortableRow
                    key={cat._key ?? cat.title}
                    category={cat}
                    wellnessItems={wellnessItems}
                    visibleColumns={visibleColumns}
                    getColumnWidth={tablePrefs.getColumnWidth}
                    onClick={handleRowClick}
                  />
                ))}
              </SortableContext>
            </DndContext>
            {categories.length === 0 && (
              <TableRow>
                <TableCell colSpan={visibleColumns.length + 1} sx={{ textAlign: 'center', color: 'text.secondary', py: 2 }}>
                  No categories added yet
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
      <RowContextMenu
        position={menuPosition}
        open={!!menuPosition}
        options={contextMenuOptions}
        onClose={handleMenuClose}
      />
    </Box>
  );
};

export default WellnessCategoriesTab;
